import type { QueryClient } from '@tanstack/react-query';

/**
 * Query key for "does this item already have a roast profile?".
 *
 * GET /work-orders/recipes answers that question two ways — by work order id or by
 * reference id — and a caller has exactly one of them, so the mode is part of the key.
 * Both sit under a shared prefix so one invalidation reaches every button on screen.
 */
export function roastProfileExistenceKey(lookup: {
  workOrderId?: string | null;
  referenceId?: string | null;
}): (string | null | undefined)[] {
  return lookup.workOrderId
    ? ['work-order-recipe', 'existence', 'by-work-order', lookup.workOrderId]
    : ['work-order-recipe', 'existence', 'by-reference', lookup.referenceId];
}

/**
 * Drops every cached roast-profile existence answer.
 *
 * Saving a roast profile is what makes those answers wrong, so the save calls this —
 * not the screens around it. InventoryRoastProfileButton used to hold the answer in
 * component state keyed on the item id, which never changes while the row stays
 * mounted: the button kept rendering "Add" until the user reloaded the page (INV-021).
 *
 * The whole prefix goes rather than the one key that was written, because the writer and
 * the reader do not always hold the same id — a Roasted Bulk row opens the modal with
 * its own work order id while the button next to it looks the profile up by the
 * inventory item's woReferenceId. These are one small request per visible row, and a
 * save is rare, so refetching all of them is the cheap way to be correct either way.
 */
export function invalidateRoastProfileExistence(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['work-order-recipe'] });
}
